import lodash from 'lodash'
import { unref } from 'vue-demi'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import { displayI18nFullName } from '@/utils/fullName'
import { UserSource, TENANT_TENANCY_TABS } from '@/utils/consts'
import { useStoreGetters } from '@/store'
import { CCD_TENANCY_CONTACT_TYPES_MAP } from './consts'

// This function is for linked leases tab display order with leaseType
export function sortLeaseWithType (leases) {
  return lodash.orderBy(leases || [], ['active', (item) => orderByLeaseType(item), 'buildAbbr', 'leaseStart'], ['desc', 'asc', 'asc', 'asc'])
}

function orderByLeaseType (lease) {
  const typeOrder = ['LO', 'LS', 'LP', 'TO', 'TS', 'SO', 'SS']
  return typeOrder.findIndex(type => type === lease.leaseType)
}

export function concatLeasesAsPremises (tenancy) {
  const premisesArray = []
  sortLeaseWithType(tenancy.leases).forEach(lease => {
    if (dateUtil.isEffective(lease.leaseStart, lease.leaseEnd) && !premisesArray.includes(lease.premises)) {
      premisesArray.push(lease.premises)
    }
  })
  return premisesArray.join(' || ')
}

/**
 * @template {{ effectiveDate: string, endDate: string, floor: string }} PD
 * @param {{ premisesDetailList: PD[] }} tenancy
 * @param {(pd: PD) => boolean} filterFn
 * @returns
 */
function getFloorsInTenancy (tenancy, filterFn = (item) => !!item) {
  const list = tenancy.premisesDetailList
  return lodash.uniq(list.filter(filterFn).map((item) => item.floor))
}

export function getAllFloorsInTenancy (tenancy) {
  return getFloorsInTenancy(tenancy)
}

export function getInactiveFloorsInTenancy (tenancy) {
  return getFloorsInTenancy(tenancy, (item) => !dateUtil.isEffective(item.effectiveDate, item.endDate))
}

export function getActiveFloorsInTenancy (tenancy) {
  return getFloorsInTenancy(tenancy, (item) => dateUtil.isEffective(item.effectiveDate, item.endDate))
}

export function getActiveAndFutureFloors (tenancy) {
  return getFloorsInTenancy(tenancy, (item) => {
    return dateUtil.isEffective(item.effectiveDate, item.endDate) || new Date(item.effectiveDate) > new Date()
  })
}

export function generateHotbDisplayName (currentTenancy, hotb) {
  const buildingCode = lodash.get(currentTenancy, 'building.code')
  const transactionDate = dateUtil.formatDate(hotb.transactionDate) || i18n.t('emptyValue')
  const hotbType = i18n.t(`tenancyProject.${lodash.toLower(hotb.type)}`)
  const of = i18n.t('prepositions.of')
  const name = `${lodash.capitalize(hotbType)} ${of}${buildingCode} ${hotb.premises} (${transactionDate})`
  return name
}

export function getTenancyDisplayName (tenancy, user) {
  let name = user.displayName
  if (tenancy && tenancy.accounts && user.source === UserSource.EXT) {
    const account = tenancy.accounts.find(account => account.id === user.id)
    if (account) name = displayI18nFullName(account.TenancyAccount.contactFirstName, account.TenancyAccount.contactLastName)
  }
  return name
}

function canAccessStandaloneWP (tenancyAccounts, user) {
  return (tenancyAccounts || []).some(account =>
    account.id === user.id && !!lodash.get(account, 'TenancyAccount.perms.TenantProject', false))
}

export function getTenantTabIndexByName (tabName) {
  let tenantTabName = Object.values(TENANT_TENANCY_TABS)
  const { enableWorkPermitsFeature, enableTenantProjectsFeature } = useStoreGetters('regions', ['enableWorkPermitsFeature', 'enableTenantProjectsFeature'])
  const { currentTenancy, canAccessAllHotbs, canAccessAllProjects } = useStoreGetters('ccd', ['currentTenancy', 'canAccessAllHotbs', 'canAccessAllProjects'])
  const { currentUser } = useStoreGetters('user', ['currentUser'])

  const accounts = lodash.get(currentTenancy, 'value.accounts', [])
  const canAccessSA = canAccessStandaloneWP(accounts, currentUser.value)
  if (!enableWorkPermitsFeature.value || !canAccessSA) {
    tenantTabName.splice(tenantTabName.findIndex(name => name === TENANT_TENANCY_TABS.standaloneWorkPermits), 1)
  }

  if (!enableTenantProjectsFeature.value || (!canAccessAllProjects.value && !canAccessAllHotbs.value)) {
    tenantTabName.splice(tenantTabName.findIndex(name => name === TENANT_TENANCY_TABS.allProjects), 1)
  }

  const index = tenantTabName.findIndex(name => name === tabName)
  return index > -1 ? index : 0
}

const getContactTypesMap = (checkConditionMap) => {
  return Object.entries(CCD_TENANCY_CONTACT_TYPES_MAP).reduce((acc, [key, { i18nKey }]) => {
    acc[key] = {
      i18nKey: i18nKey,
    }
    acc[key].checkCondition = checkConditionMap['isCircular']
    return acc
  }, {})
}

export const calcContactRoleType = (item, delimiter = ', ') => {
  const { enableEcircularFeature } = useStoreGetters('regions', ['enableEcircularFeature'])
  const contactTypesMap = getContactTypesMap({
    isCircular: () => unref(enableEcircularFeature),
  })
  const result = Object.keys(contactTypesMap).map(key => {
    const el = contactTypesMap[key]
    const checkingFn = lodash.get(el, 'checkCondition', () => true)
    if (lodash.get(item, `CompanyContact.${key}`) && checkingFn()) {
      const i18nKey = `company.contactTable.${contactTypesMap[key].i18nKey}`
      return i18n.t(i18nKey)
    } else {
      return false
    }
  }).filter(Boolean)
  return result.length > 0 ? result.join(delimiter) : i18n.t('emptyValue')
}
